import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildCommitPlanOrder,
  exportComposePlanJson,
  listActiveComponents,
  listReadyProducts,
  listUnfinishedProducts,
  planMatchStats,
  saveComposePlan,
  suggestMatchesForNode,
  type ComposeEntryMode,
  type ComposePlan,
  type ComposePlanStep,
  type ComposePlanSummary,
} from "@/lib/manufacturing/composePlan";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import {
  completeComposePlanRemote,
  createComposePlanRemote,
  discardComposePlanRemote,
  fetchComposePlanSummaries,
  importComposePlanRemote,
  persistComposePlanRemote,
  resumeComposePlanRemote,
} from "./composePlanRemoteActions";
import { expandComposePlan, runComposePartialCommit } from "./composePlanWorkflow";
import { useComposeMatchActions } from "./useComposeMatchActions";

const REMOTE_SAVE_DEBOUNCE_MS = 800;

export function useManufacturingModelsComposePlan({
  orgId,
  recipes,
  initialGroupId,
  initialGroupCode,
  initialGroupName,
}: {
  orgId: string;
  recipes: readonly MfgRecipeSummary[];
  initialGroupId?: string;
  initialGroupCode?: string;
  initialGroupName?: string;
}) {
  const [plan, setPlan] = useState<ComposePlan | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedCommitIds, setSelectedCommitIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commitLog, setCommitLog] = useState<string | null>(null);
  const [stylesRaw, setStylesRaw] = useState("");
  const [briefRaw, setBriefRaw] = useState("");
  const [savedPlans, setSavedPlans] = useState<ComposePlanSummary[]>([]);
  const [suggestionsRevision, setSuggestionsRevision] = useState(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextRemoteSave = useRef(false);
  const canRemote = Boolean(orgId && orgId !== "local");

  const refreshSavedPlans = useCallback(async () => {
    if (!canRemote) {
      setSavedPlans([]);
      return;
    }
    const res = await fetchComposePlanSummaries();
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setSavedPlans(res.plans);
  }, [canRemote]);

  useEffect(() => {
    void refreshSavedPlans();
  }, [refreshSavedPlans]);

  useEffect(() => {
    if (!plan || !canRemote) return;
    if (skipNextRemoteSave.current) {
      skipNextRemoteSave.current = false;
      saveComposePlan(plan);
      return;
    }
    saveComposePlan(plan);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void persistComposePlanRemote(plan).then((r) => {
        if (!r.ok) setError(r.error);
      });
    }, REMOTE_SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [plan, canRemote]);

  const adoptPlan = useCallback((loaded: ComposePlan) => {
    skipNextRemoteSave.current = true;
    setPlan(loaded);
    setStylesRaw(loaded.archetype.styles.length ? loaded.archetype.styles.join("\n") : "");
    setSelectedNodeId(null);
    setSelectedCommitIds(listReadyProducts(loaded).map((p) => p.id));
    setError(null);
    setCommitLog(null);
    saveComposePlan(loaded);
  }, []);

  const start = useCallback(
    async (entryMode: ComposeEntryMode) => {
      if (!canRemote) {
        setError("Compose plans require an active organization.");
        return;
      }
      setBusy(true);
      setError(null);
      try {
        const res = await createComposePlanRemote({
          orgId,
          entryMode,
          recipeGroupId: initialGroupId ?? null,
          seriesCode: initialGroupCode ?? "",
          seriesName: initialGroupName ?? "",
        });
        if (!res.ok) {
          setError(res.error);
          return;
        }
        adoptPlan(res.plan);
        void refreshSavedPlans();
      } finally {
        setBusy(false);
      }
    },
    [adoptPlan, canRemote, orgId, initialGroupId, initialGroupCode, initialGroupName, refreshSavedPlans],
  );

  const resume = useCallback(
    async (planId: string) => {
      if (!canRemote) return;
      setBusy(true);
      setError(null);
      try {
        const res = await resumeComposePlanRemote(orgId, planId);
        if (!res.ok) {
          setError(res.error);
          void refreshSavedPlans();
          return;
        }
        adoptPlan(res.plan);
      } finally {
        setBusy(false);
      }
    },
    [adoptPlan, canRemote, orgId, refreshSavedPlans],
  );

  const discardSaved = useCallback(
    async (planId: string) => {
      if (canRemote) {
        const res = await discardComposePlanRemote(orgId || "local", planId);
        if (!res.ok) setError(res.error);
      }
      void refreshSavedPlans();
      if (plan?.planId === planId) setPlan(null);
    },
    [canRemote, orgId, plan?.planId, refreshSavedPlans],
  );

  const setStep = useCallback((step: ComposePlanStep) => {
    setPlan((p) => {
      if (!p) return p;
      const next = { ...p, step, updatedAt: new Date().toISOString() };
      if (step === "commit") {
        setSelectedCommitIds(listReadyProducts(next).map((x) => x.id));
      }
      return next;
    });
  }, []);

  const patchArchetype = useCallback((patch: Partial<ComposePlan["archetype"]>) => {
    setPlan((p) =>
      p
        ? {
            ...p,
            archetype: { ...p.archetype, ...patch },
            updatedAt: new Date().toISOString(),
          }
        : p,
    );
  }, []);

  const expand = useCallback(() => {
    setPlan((p) => {
      if (!p) return p;
      const res = expandComposePlan(p, stylesRaw);
      if (!res.ok) {
        setError(res.error);
        return p;
      }
      setError(null);
      return res.plan;
    });
  }, [stylesRaw]);

  const selectedNode = useMemo(
    () => plan?.nodes.find((n) => n.id === selectedNodeId) ?? null,
    [plan, selectedNodeId],
  );
  const suggestions = useMemo(() => {
    if (!plan || !selectedNode) return [];
    return suggestMatchesForNode(selectedNode, recipes, {
      boosts: plan.matchBoosts,
      confirmedLinks: plan.confirmedLinks,
      kindFilter:
        selectedNode.role === "product"
          ? ["product"]
          : selectedNode.kind === "package"
            ? ["package", "assembly"]
            : ["assembly", "package", "option", "variant"],
    });
    // suggestionsRevision forces a recompute after Refresh (and after selection changes).
  }, [plan, selectedNode, recipes, suggestionsRevision]);

  const refreshSuggestions = useCallback(() => {
    setSuggestionsRevision((n) => n + 1);
  }, []);

  // When the selected node changes, refresh suggestions for that slot/size.
  useEffect(() => {
    if (!selectedNodeId) return;
    setSuggestionsRevision((n) => n + 1);
  }, [selectedNodeId]);

  const {
    matchNode,
    matchNodes,
    reclassify,
    splitByStyle,
    splitByStyleMany,
    removeProduct,
  } = useComposeMatchActions({
    recipes,
    setPlan,
    setError,
    setCommitLog,
  });

  const commitSelected = useCallback(async () => {
    if (!plan) return null;
    setBusy(true);
    setError(null);
    try {
      const res = await runComposePartialCommit(plan, recipes, selectedCommitIds);
      if (!res.ok) {
        setError(res.message);
        return { ok: false as const, message: res.message, result: res.result };
      }
      setCommitLog(res.message);
      setPlan(res.plan);
      setSelectedCommitIds(res.nextReadyIds);
      const stillVisible = new Set([
        ...listUnfinishedProducts(res.plan).map((p) => p.id),
        ...listActiveComponents(res.plan).map((c) => c.id),
      ]);
      setSelectedNodeId((id) => (id && stillVisible.has(id) ? id : null));
      return {
        ok: true as const,
        message: res.message,
        result: res.result,
        remaining: res.remaining,
        allDone: res.allDone,
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Commit failed";
      setError(msg);
      return { ok: false as const, message: msg };
    } finally {
      setBusy(false);
    }
  }, [plan, recipes, selectedCommitIds]);

  const finishPlan = useCallback(async () => {
    if (!plan) return;
    if (canRemote) await completeComposePlanRemote(plan);
    setPlan(null);
    void refreshSavedPlans();
  }, [plan, canRemote, refreshSavedPlans]);

  const exportJson = useCallback(() => (plan ? exportComposePlanJson(plan) : ""), [plan]);

  const importJson = useCallback(
    async (raw: string) => {
      if (!canRemote) {
        setError("Compose plans require an active organization.");
        return false;
      }
      setBusy(true);
      try {
        const res = await importComposePlanRemote(orgId, raw);
        if (!res.ok) {
          setError(res.error);
          return false;
        }
        adoptPlan(res.plan);
        void refreshSavedPlans();
        return true;
      } finally {
        setBusy(false);
      }
    },
    [adoptPlan, canRemote, orgId, refreshSavedPlans],
  );

  const stats = plan ? planMatchStats(plan) : null;
  const readyProducts = plan ? listReadyProducts(plan) : [];
  const order = plan
    ? buildCommitPlanOrder(plan, {
        productNodeIds: selectedCommitIds.length
          ? selectedCommitIds
          : readyProducts.map((p) => p.id),
      })
    : null;

  return {
    plan,
    stylesRaw,
    setStylesRaw,
    briefRaw,
    setBriefRaw,
    selectedNodeId,
    setSelectedNodeId,
    selectedNode,
    selectedCommitIds,
    setSelectedCommitIds,
    suggestions,
    refreshSuggestions,
    busy,
    error,
    setError,
    commitLog,
    stats,
    order,
    readyProducts,
    savedPlans,
    components: plan ? listActiveComponents(plan) : [],
    products: plan ? listUnfinishedProducts(plan) : [],
    start,
    resume,
    discardSaved,
    setStep,
    patchArchetype,
    expand,
    matchNode,
    matchNodes,
    reclassify,
    splitByStyle,
    splitByStyleMany,
    removeProduct,
    commitSelected,
    finishPlan,
    exportJson,
    importJson,
    setPlan,
    refreshSavedPlans,
  };
}
